/**
 * タイルレイヤーの読み込み結果から「現在の表示範囲にデータがあるか」を判定する純関数群。
 *
 * 地理院の写真タイルはデータがない範囲を 404 で返すため、Leaflet の tileerror を
 * 集計すれば追加リクエストなしで「この範囲に写真はない」と分かる。
 */

export type TileLoadState = "pending" | "loaded" | "error";

export interface TileCoords {
  z: number;
  x: number;
  y: number;
}

export interface TileRecord extends TileCoords {
  state: TileLoadState;
}

/**
 * 表示範囲のデータ有無
 * - loading: まだ判定できない（読み込み中、またはタイル未要求）
 * - full: 要求した全タイルにデータがある
 * - partial: 一部のタイルにデータがない
 * - none: 要求した全タイルにデータがない
 * - out-of-zoom: レイヤーの配信ズーム範囲外でタイルを要求していない
 */
export type TileCoverageStatus =
  | "loading"
  | "full"
  | "partial"
  | "none"
  | "out-of-zoom";

export const tileKey = ({ z, x, y }: TileCoords): string => `${z}/${x}/${y}`;

/**
 * 現在のタイルズーム（currentZ）に属するタイルだけを見て有無を判定する。
 * Leaflet はズーム変更後もしばらく前のズームのタイルを保持するため、それらは除外する。
 */
export const computeTileCoverage = (
  tiles: Iterable<TileRecord>,
  currentZ: number | null
): Exclude<TileCoverageStatus, "out-of-zoom"> => {
  if (currentZ === null) return "loading";

  let total = 0;
  let errors = 0;
  for (const tile of tiles) {
    if (tile.z !== currentZ) continue;
    if (tile.state === "pending") return "loading";
    total += 1;
    if (tile.state === "error") errors += 1;
  }

  if (total === 0) return "loading";
  if (errors === 0) return "full";
  if (errors === total) return "none";
  return "partial";
};
